import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { theme } from 'styles/theme';
import { ActionButton } from 'styles/common.styles';
import DraggableDialog from 'components/common/DraggableDialog';
import { ModeTabBar, ModeTab } from 'components/system/SystemPageChrome.styles';
import { useAuth } from 'contexts/AuthContext';
import { showAlert, showConfirm } from 'utils/toastBridge';
import { useGridExportRegistry } from 'components/grid/excel';
import {
  PRINT_ALIGN_OPTIONS,
  PRINT_BAND_FONT_SIZE_OPTIONS,
  PRINT_COL_WIDTH_MAX,
  PRINT_COL_WIDTH_MIN,
  PRINT_FONT_OPTIONS,
  PRINT_FONT_SIZE_OPTIONS,
  PRINT_ORIENT_OPTIONS,
  PRINT_PAPER_OPTIONS,
  PRINT_STAMP_BUILTIN,
  PRINT_STAMP_COUNTS,
  PRINT_STAMP_POSITIONS,
  PRINT_TOKENS,
  clampColMm,
  defaultStamp,
  loadPrintLayout,
  savePrintLayout,
  defaultPrintLayout,
  todayYmd,
} from './printLayout';
import {
  deleteStampType,
  listStampTypeOptions,
  upsertStampType,
} from './printStampTypes';
import { buildPrintModel, getPrintColumnDefs } from './printModel';
import { buildPrintHtml } from './printHtml';
import { bindPrintColumnResize } from './printPreviewResize';
import { PRINT_FORMATS, renderPrintFormat } from './printFormats';

const TABS = [
  { id: 'doc', label: '문서' },
  { id: 'col', label: '컬럼' },
  { id: 'header', label: '머릿글' },
  { id: 'footer', label: '꼬릿말' },
  { id: 'stamp', label: '결재선' },
];

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  height: 100%;
  min-height: 0;
`;

const Split = styled.div`
  display: flex;
  gap: 12px;
  flex: 1;
  min-height: 0;
`;

const Pane = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  flex: ${(p) => p.$flex || 1};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  padding: 8px 10px 10px;
  overflow: hidden;
  position: relative;
  z-index: ${(p) => p.$layer || 0};
`;

const PaneTitle = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin-bottom: 6px;
  flex-shrink: 0;
`;

const FormScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding-top: 8px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
`;

const Label = styled.label`
  flex: 0 0 56px;
  text-align: right;
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.primary};
`;

const Input = styled.input`
  flex: 1;
  min-width: 0;
  height: 26px;
  padding: 0 6px;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.primary};
  background: ${theme.colors.white};
`;

const NumInput = styled(Input)`
  flex: 0 0 56px;
`;

const Select = styled.select`
  height: 26px;
  padding: 0 4px;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.primary};
  background: ${theme.colors.white};
`;

const CheckItem = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: ${theme.typography.fontSize.sm};
  padding: 2px 0;
  color: ${theme.colors.text.primary};
  cursor: pointer;
`;

const Hint = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.secondary};
  line-height: 1.4;
`;

const PreviewHost = styled.div`
  flex: 1;
  min-height: 0;
  min-width: 0;
  position: relative;
  overflow: hidden;
  isolation: isolate;
  border: 1px solid ${theme.colors.border};
  background: ${theme.colors.white};
`;

const PreviewFrame = styled.iframe`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  min-width: 0;
  border: 0;
  background: ${theme.colors.white};
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const FormatCluster = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
`;

const TokenRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin: 0 0 8px 64px;
`;

const ColRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 2px 0;
`;

const ColName = styled.span`
  flex: 1;
  min-width: 0;
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.primary};
`;

function BandZoneEditor({ cell, onChange, onFocus }) {
  return (
    <>
      <Input
        value={cell?.text || ''}
        onChange={(e) => onChange({ text: e.target.value })}
        onFocus={onFocus}
      />
      <Select
        value={cell?.align || 'left'}
        onChange={(e) => onChange({ align: e.target.value })}
      >
        {PRINT_ALIGN_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </Select>
      <Select
        value={cell?.fontSizePt || 9}
        onChange={(e) => onChange({ fontSizePt: Number(e.target.value) })}
      >
        {PRINT_BAND_FONT_SIZE_OPTIONS.map((n) => (
          <option key={n} value={n}>
            {n}pt
          </option>
        ))}
      </Select>
    </>
  );
}

export default function GridPrintDialog({
  open,
  onClose,
  fileName = '목록출력',
  prefsUserId = '',
  defaultTitle = '',
}) {
  const { user, compCd } = useAuth();
  const registry = useGridExportRegistry();
  const focused = registry.getFocused();
  const grids = registry.list();
  const handle = focused || grids[0] || null;
  const [tab, setTab] = useState('doc');
  const [bandSlot, setBandSlot] = useState('left');
  const [stampName, setStampName] = useState('');
  const [stampTick, setStampTick] = useState(0);
  const iframeRef = useRef(null);
  const lastFocusRef = useRef(null);
  const handleId = handle?.id || '';

  const availableCols = useMemo(
    () => (open ? getPrintColumnDefs(handle, prefsUserId) : []),
    [open, handle, prefsUserId, registry.version]
  );

  const stampOptions = useMemo(
    () => (open ? listStampTypeOptions(prefsUserId) : []),
    [open, prefsUserId, stampTick]
  );

  const [layout, setLayout] = useState(() =>
    defaultPrintLayout({ title: defaultTitle, columnIds: [] })
  );

  useEffect(() => {
    if (!open || !handleId) return;
    setTab('doc');
    const all = getPrintColumnDefs(handle, prefsUserId);
    const defaultIds = all.filter((c) => !c.hidden).map((c) => c.field);
    const loaded = loadPrintLayout(handleId, prefsUserId, {
      title: defaultTitle || handle?.label || fileName,
      columnIds: defaultIds,
    });
    const keep = (loaded.columnIds || []).filter((id) => all.some((c) => c.field === id));
    setLayout({
      ...loaded,
      title: loaded.title || defaultTitle || handle?.label || '',
      columnIds: keep.length ? keep : defaultIds,
    });
    setStampName('');
    // 그리드 핸들 객체 참조가 바뀌어도 같은 화면이면 입력을 되돌리지 않는다
  }, [open, handleId, prefsUserId, defaultTitle, fileName]);

  const printCtx = useMemo(
    () => ({
      today: todayYmd(),
      screenName: handle?.label || defaultTitle || layout.title || '',
      tenantCode: compCd || '',
      tenantName: user?.compNm || compCd || '',
      userName: user?.name || user?.username || '',
      page: 1,
      pageCount: 1,
    }),
    [handle, defaultTitle, layout.title, compCd, user]
  );

  const patch = (partial) => setLayout((prev) => ({ ...prev, ...partial }));

  const patchMargin = (key, value) => {
    setLayout((prev) => ({
      ...prev,
      marginMm: { ...prev.marginMm, [key]: value },
    }));
  };

  const patchBand = (band, slot, partial) => {
    setLayout((prev) => ({
      ...prev,
      [band]: {
        ...prev[band],
        [slot]: { ...prev[band][slot], ...partial },
      },
    }));
  };

  const patchStamp = (partial) => {
    setLayout((prev) => ({
      ...prev,
      stamp: { ...prev.stamp, ...partial },
    }));
  };

  const toggleCol = (field) => {
    setLayout((prev) => {
      const has = prev.columnIds.includes(field);
      if (has) return { ...prev, columnIds: prev.columnIds.filter((id) => id !== field) };
      return { ...prev, columnIds: [...prev.columnIds, field] };
    });
  };

  const moveCol = (field, dir) => {
    setLayout((prev) => {
      const ids = [...prev.columnIds];
      const i = ids.indexOf(field);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= ids.length) return prev;
      [ids[i], ids[j]] = [ids[j], ids[i]];
      return { ...prev, columnIds: ids };
    });
  };

  const applyStampType = (typeId) => {
    const found = stampOptions.find((t) => t.id === typeId);
    if (!found) return;
    patchStamp({
      cellCount: found.cellCount,
      titles: [...found.titles],
      presetId: found.id,
    });
    setStampName(found.builtin ? '' : found.name);
  };

  const changeCellCount = (n) => {
    const count = Number(n);
    const builtin = PRINT_STAMP_BUILTIN[count] || PRINT_STAMP_BUILTIN[3];
    patchStamp({
      cellCount: count,
      titles: [...builtin],
      presetId: `builtin-${count}`,
    });
    setStampName('');
  };

  const changeStampTitle = (idx, value) => {
    setLayout((prev) => {
      const titles = [...(prev.stamp?.titles || [])];
      titles[idx] = value;
      return { ...prev, stamp: { ...prev.stamp, titles } };
    });
  };

  const saveCurrentStampType = () => {
    const stamp = layout.stamp || defaultStamp(3);
    const saved = upsertStampType(prefsUserId, {
      id: stamp.presetId,
      name: stampName || `${stamp.cellCount}칸`,
      cellCount: stamp.cellCount,
      titles: stamp.titles,
    });
    if (!saved) {
      showAlert('결재선 유형을 저장할 수 없습니다.', 'warning');
      return;
    }
    patchStamp({ presetId: saved.id, titles: saved.titles, cellCount: saved.cellCount });
    setStampName(saved.name);
    setStampTick((n) => n + 1);
    showAlert('결재선 유형을 저장했습니다.', 'success');
  };

  const removeCurrentStampType = async () => {
    const id = layout.stamp?.presetId;
    if (!id || String(id).startsWith('builtin-')) {
      showAlert('기본 유형은 삭제할 수 없습니다.', 'warning');
      return;
    }
    const ok = await showConfirm('이 결재선 유형을 삭제할까요?');
    if (!ok) return;
    deleteStampType(prefsUserId, id);
    changeCellCount(layout.stamp?.cellCount || 3);
    setStampTick((n) => n + 1);
    showAlert('결재선 유형을 삭제했습니다.', 'success');
  };

  const model = useMemo(() => {
    if (!open || !handle) return null;
    return buildPrintModel(handle, layout, prefsUserId);
  }, [open, handle, layout, prefsUserId]);

  const previewHtml = useMemo(() => {
    if (!open || !model) return '';
    return buildPrintHtml(model, layout, printCtx);
  }, [open, model, layout, printCtx]);

  useEffect(() => {
    if (!open) return undefined;
    const iframe = iframeRef.current;
    if (!iframe) return undefined;
    const html = previewHtml || '<!DOCTYPE html><html><body></body></html>';
    const restore = () => {
      const el = lastFocusRef.current;
      if (el && typeof el.focus === 'function') {
        try {
          el.focus({ preventScroll: true });
        } catch {
          /* ignore */
        }
      }
    };
    const doc = iframe.contentDocument;
    if (doc) {
      doc.open();
      doc.write(html);
      doc.close();
      requestAnimationFrame(restore);
      const unbind = bindPrintColumnResize(doc, {
        layout,
        onCommit: (widths) => {
          setLayout((prev) => ({
            ...prev,
            columnWidthsMm: widths.columnWidthsMm,
            rowNumberWidthMm: widths.rowNumberWidthMm,
          }));
        },
      });
      return () => {
        unbind();
      };
    }
    iframe.srcdoc = html;
    iframe.addEventListener('load', restore, { once: true });
    return () => iframe.removeEventListener('load', restore);
  }, [open, previewHtml]);

  const persist = () => {
    if (handle?.id) savePrintLayout(handle.id, prefsUserId, layout);
  };

  const runFormat = (formatId) => {
    if (!handle) {
      showAlert('출력할 그리드가 없습니다.', 'warning');
      return;
    }
    if (!layout.columnIds.length) {
      showAlert('출력할 항목을 선택하세요.', 'warning');
      return;
    }
    persist();
    const current = buildPrintModel(handle, layout, prefsUserId);
    const result = renderPrintFormat(formatId, current, layout, fileName, printCtx);
    if (!result.ok) {
      showAlert(result.reason || '출력할 수 없습니다.', 'warning');
    }
  };

  const stamp = layout.stamp || defaultStamp(3);
  const selectedCols = layout.columnIds
    .map((id) => availableCols.find((c) => c.field === id))
    .filter(Boolean);
  const unusedCols = availableCols.filter((c) => !layout.columnIds.includes(c.field));

  return (
    <DraggableDialog
      open={open}
      title="출력 — 문서 설정"
      onClose={onClose}
      dialogWidth="1100px"
      dialogHeight="720px"
      keepMounted={false}
      prefsUserId={prefsUserId}
      sizeKey="grid-print-form"
    >
      <Body>
        <Hint>
          F7 조회 후 그리드에 보이는 행을 문서 레이아웃으로 출력합니다. 설정은 화면·사용자별로 기억됩니다.
          {' · '}대상: <strong>{handle?.label || '-'}</strong>
          {model ? ` · ${model.rowCount}행` : ''}
          {' · '}미리보기 표 헤더 오른쪽 경계를 끌어 열 폭을 조절합니다.
        </Hint>
        <Split>
          <Pane
            $flex={1.05}
            $layer={2}
            onFocusCapture={(e) => {
              lastFocusRef.current = e.target;
            }}
          >
            <ModeTabBar>
              {TABS.map((t) => (
                <ModeTab
                  key={t.id}
                  type="button"
                  $active={tab === t.id}
                  onClick={() => setTab(t.id)}
                >
                  {t.label}
                </ModeTab>
              ))}
            </ModeTabBar>
            <FormScroll>
              {tab === 'doc' && (
                <>
                  <Row>
                    <Label htmlFor="print-title">제목</Label>
                    <Input
                      id="print-title"
                      value={layout.title}
                      onChange={(e) => patch({ title: e.target.value })}
                    />
                  </Row>
                  <Row>
                    <Label>용지</Label>
                    <Select
                      value={layout.paper}
                      onChange={(e) => patch({ paper: e.target.value })}
                    >
                      {PRINT_PAPER_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </Select>
                    <Select
                      value={layout.orientation}
                      onChange={(e) => patch({ orientation: e.target.value })}
                    >
                      {PRINT_ORIENT_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </Select>
                  </Row>
                  <Row>
                    <Label>여백</Label>
                    <Hint as="span">상</Hint>
                    <NumInput
                      type="number"
                      min={5}
                      max={40}
                      value={layout.marginMm?.top ?? 15}
                      onChange={(e) => patchMargin('top', Number(e.target.value))}
                    />
                    <Hint as="span">우</Hint>
                    <NumInput
                      type="number"
                      min={5}
                      max={40}
                      value={layout.marginMm?.right ?? 15}
                      onChange={(e) => patchMargin('right', Number(e.target.value))}
                    />
                    <Hint as="span">하</Hint>
                    <NumInput
                      type="number"
                      min={5}
                      max={40}
                      value={layout.marginMm?.bottom ?? 15}
                      onChange={(e) => patchMargin('bottom', Number(e.target.value))}
                    />
                    <Hint as="span">좌</Hint>
                    <NumInput
                      type="number"
                      min={5}
                      max={40}
                      value={layout.marginMm?.left ?? 15}
                      onChange={(e) => patchMargin('left', Number(e.target.value))}
                    />
                    <Hint as="span">mm</Hint>
                  </Row>
                  <Row>
                    <Label>글꼴</Label>
                    <Select
                      value={layout.fontFamily}
                      onChange={(e) => patch({ fontFamily: e.target.value })}
                    >
                      {PRINT_FONT_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </Select>
                    <Select
                      value={layout.fontSizePt}
                      onChange={(e) => patch({ fontSizePt: Number(e.target.value) })}
                    >
                      {PRINT_FONT_SIZE_OPTIONS.map((n) => (
                        <option key={n} value={n}>
                          {n}pt
                        </option>
                      ))}
                    </Select>
                  </Row>
                  <Row>
                    <Label />
                    <CheckItem>
                      <input
                        type="checkbox"
                        checked={layout.showRowNumber}
                        onChange={(e) => patch({ showRowNumber: e.target.checked })}
                      />
                      행번호
                    </CheckItem>
                  </Row>
                </>
              )}

              {tab === 'col' && (
                <>
                  <Hint>
                    선택한 항목만 표에 나갑니다. 위/아래로 순서, 미리보기에서 열 폭(mm)을 조절합니다.
                  </Hint>
                  {layout.showRowNumber !== false && (
                    <ColRow>
                      <ColName>행번호</ColName>
                      <NumInput
                        type="number"
                        min={PRINT_COL_WIDTH_MIN}
                        max={PRINT_COL_WIDTH_MAX}
                        value={layout.rowNumberWidthMm ?? 12}
                        onChange={(e) =>
                          patch({ rowNumberWidthMm: clampColMm(e.target.value, 12) })
                        }
                      />
                      <Hint as="span">mm</Hint>
                    </ColRow>
                  )}
                  {selectedCols.map((c, idx) => (
                    <ColRow key={c.field}>
                      <input
                        type="checkbox"
                        checked
                        onChange={() => toggleCol(c.field)}
                      />
                      <ColName>
                        {c.header || c.field}
                        {c.hidden ? ' (숨김 열)' : ''}
                      </ColName>
                      <NumInput
                        type="number"
                        min={PRINT_COL_WIDTH_MIN}
                        max={PRINT_COL_WIDTH_MAX}
                        value={layout.columnWidthsMm?.[c.field] ?? ''}
                        placeholder="자동"
                        onChange={(e) => {
                          const raw = e.target.value;
                          setLayout((prev) => {
                            const next = { ...(prev.columnWidthsMm || {}) };
                            if (raw === '') delete next[c.field];
                            else next[c.field] = clampColMm(raw, 20);
                            return { ...prev, columnWidthsMm: next };
                          });
                        }}
                      />
                      <Hint as="span">mm</Hint>
                      <ActionButton
                        type="button"
                        disabled={idx === 0}
                        onClick={() => moveCol(c.field, -1)}
                      >
                        위
                      </ActionButton>
                      <ActionButton
                        type="button"
                        disabled={idx === selectedCols.length - 1}
                        onClick={() => moveCol(c.field, 1)}
                      >
                        아래
                      </ActionButton>
                    </ColRow>
                  ))}
                  {unusedCols.length > 0 && (
                    <>
                      <PaneTitle as="div" style={{ marginTop: 10 }}>
                        추가할 열
                      </PaneTitle>
                      {unusedCols.map((c) => (
                        <CheckItem key={c.field}>
                          <input
                            type="checkbox"
                            checked={false}
                            onChange={() => toggleCol(c.field)}
                          />
                          {c.header || c.field}
                          {c.hidden ? ' (숨김 열)' : ''}
                        </CheckItem>
                      ))}
                    </>
                  )}
                </>
              )}

              {(tab === 'header' || tab === 'footer') && (
                <>
                  <Hint>
                    {tab === 'header'
                      ? '머릿글 3칸입니다. 1차에는 임의 표를 넣지 않습니다. 표는 결재선만 사용합니다.'
                      : '꼬릿말 3칸입니다. 미리보기에서 {페이지}/{전체쪽}은 1 / 1로 보입니다.'}
                  </Hint>
                  <TokenRow>
                    {PRINT_TOKENS.map((t) => (
                      <ActionButton
                        key={t.token}
                        type="button"
                        title={t.token}
                        onClick={() =>
                          patchBand(tab, bandSlot, {
                            text: `${layout[tab]?.[bandSlot]?.text || ''}${t.token}`,
                          })
                        }
                      >
                        {t.label}
                      </ActionButton>
                    ))}
                    <Hint as="span">
                      {bandSlot === 'left' ? '왼쪽' : bandSlot === 'center' ? '가운데' : '오른쪽'}
                      칸에 넣습니다. 칸을 클릭한 뒤 변수를 누르세요.
                    </Hint>
                  </TokenRow>
                  {['left', 'center', 'right'].map((slot) => (
                    <Row key={slot}>
                      <Label>
                        {slot === 'left' ? '왼쪽' : slot === 'center' ? '가운데' : '오른쪽'}
                      </Label>
                      <BandZoneEditor
                        cell={layout[tab]?.[slot]}
                        onFocus={() => setBandSlot(slot)}
                        onChange={(partial) => patchBand(tab, slot, partial)}
                      />
                    </Row>
                  ))}
                </>
              )}

              {tab === 'stamp' && (
                <>
                  <Hint>
                    전자결재와 연동하지 않습니다. 빈 칸과 테두리만 그립니다. 2·3·4·5칸 유형을 저장해 두고 불러올 수 있습니다.
                  </Hint>
                  <Row>
                    <Label />
                    <CheckItem>
                      <input
                        type="checkbox"
                        checked={stamp.enabled === true}
                        onChange={(e) => patchStamp({ enabled: e.target.checked })}
                      />
                      결재선 표시
                    </CheckItem>
                  </Row>
                  <Row>
                    <Label>위치</Label>
                    <Select
                      value={stamp.position || 'titleRight'}
                      onChange={(e) => patchStamp({ position: e.target.value })}
                    >
                      {PRINT_STAMP_POSITIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </Select>
                  </Row>
                  <Row>
                    <Label>유형</Label>
                    <Select
                      value={stamp.presetId || `builtin-${stamp.cellCount}`}
                      onChange={(e) => applyStampType(e.target.value)}
                    >
                      {stampOptions.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.builtin ? `${t.name} (기본)` : t.name}
                        </option>
                      ))}
                    </Select>
                    <Select
                      value={stamp.cellCount || 3}
                      onChange={(e) => changeCellCount(e.target.value)}
                    >
                      {PRINT_STAMP_COUNTS.map((n) => (
                        <option key={n} value={n}>
                          {n}칸
                        </option>
                      ))}
                    </Select>
                  </Row>
                  {(stamp.titles || []).map((title, idx) => (
                    <Row key={`st-${idx}`}>
                      <Label>{idx + 1}칸</Label>
                      <Input
                        value={title}
                        onChange={(e) => changeStampTitle(idx, e.target.value)}
                      />
                    </Row>
                  ))}
                  <Row>
                    <Label>이름</Label>
                    <Input
                      placeholder="저장할 유형 이름"
                      value={stampName}
                      onChange={(e) => setStampName(e.target.value)}
                    />
                    <ActionButton type="button" onClick={saveCurrentStampType}>
                      유형 저장
                    </ActionButton>
                    <ActionButton
                      type="button"
                      disabled={String(stamp.presetId || '').startsWith('builtin-')}
                      onClick={removeCurrentStampType}
                    >
                      유형 삭제
                    </ActionButton>
                  </Row>
                </>
              )}
            </FormScroll>
          </Pane>
          <Pane $flex={1.25} $layer={0}>
            <PaneTitle>미리보기</PaneTitle>
            <Hint>표 헤더 오른쪽 경계를 드래그하면 열 폭이 바뀝니다.</Hint>
            <PreviewHost
              onMouseDown={() => {
                lastFocusRef.current = null;
              }}
            >
              <PreviewFrame
                ref={iframeRef}
                title="출력 미리보기"
                tabIndex={-1}
                sandbox="allow-same-origin"
              />
            </PreviewHost>
          </Pane>
        </Split>
        <Footer>
          <FormatCluster>
            {PRINT_FORMATS.map((f) => (
              <ActionButton
                key={f.id}
                type="button"
                $variant={f.id === 'print' ? 'primary' : undefined}
                disabled={!f.enabled}
                title={f.title}
                onClick={() => f.enabled && runFormat(f.id)}
              >
                {f.label}
              </ActionButton>
            ))}
          </FormatCluster>
          <FormatCluster>
            <ActionButton
              type="button"
              onClick={() => {
                persist();
                showAlert('문서 설정을 저장했습니다.', 'success');
              }}
            >
              문서 설정 저장
            </ActionButton>
            <ActionButton type="button" onClick={onClose}>
              닫기
            </ActionButton>
          </FormatCluster>
        </Footer>
      </Body>
    </DraggableDialog>
  );
}
